import type { BatchFunnel, FunnelIssue } from '../types/batch-funnel.api.types';
import type { StageError } from '../types/stage-errors.api.types';

/** Raw stage-error `stage` → funnel stage key (contract §5.1, identical to the
 *  backend's funnel merge). Unknown stages group under ENRICHED, like the backend. */
const STAGE_TO_FUNNEL_KEY: Record<string, string> = {
  IMPORT: 'RECEIVED',
  IMAGE_GATE: 'RECEIVED',
  CURATION: 'CURATED',
  SPLIT: 'SPLIT_ARMS',
  SPLIT_ARMS: 'SPLIT_ARMS',
  PRE_ENRICH_SCORE: 'SCORED',
  SCORING: 'SCORED',
  SAMPLE: 'SAMPLE',
  ENRICHMENT: 'ENRICHED',
  POST_ENRICH_SCORE: 'ENRICHED',
  ROLLUPS: 'ENRICHED',
  ENRICHMENT_INSIGHTS: 'ENRICHED',
  APPROVAL: 'APPROVAL',
  DELIVERY: 'DELIVERED',
};

/** Raw stages with a translated label; anything else renders its raw value under "Other". */
export const KNOWN_ERROR_STAGES: ReadonlySet<string> = new Set(Object.keys(STAGE_TO_FUNNEL_KEY));

/** Funnel stage key a stage error belongs to (§5.1). */
export function funnelStageOf(stage: string): string {
  return STAGE_TO_FUNNEL_KEY[stage] ?? 'ENRICHED';
}

/** True while the batch is currently failed — the only time an ERROR is "current" (§5.5). */
export function isBatchFailed(funnel: BatchFunnel | undefined): boolean {
  return !!funnel && (funnel.failure !== null || funnel.verdict?.state === 'FAILED');
}

/**
 * Splits the full `/errors` history into the current attempt and earlier ones
 * (§5.5). A failed batch shows everything as current. Otherwise an ERROR can
 * only be from an earlier attempt (the run went on), while WARNINGs are always
 * current. `runId: null` entries (delivery callback) count as the latest attempt,
 * which this rule already covers.
 */
export function splitStageErrors(
  errors: StageError[],
  batchFailed: boolean,
): { current: StageError[]; earlier: StageError[] } {
  if (batchFailed) return { current: errors, earlier: [] };
  return {
    current: errors.filter((e) => e.severity !== 'ERROR'),
    earlier: errors.filter((e) => e.severity === 'ERROR'),
  };
}

/** Banner error = the first current ERROR, only while the batch is failed (§5.5 / §8). */
export function bannerStageError(errors: StageError[], batchFailed: boolean): StageError | null {
  return batchFailed ? (errors.find((e) => e.severity === 'ERROR') ?? null) : null;
}

/** Prefix of funnel issues generated from stage errors (§6). */
const STAGE_ERROR_ISSUE_PREFIX = 'STAGE_ERROR:';

/**
 * The `/errors` entry behind a funnel stage-error issue, for its ×N count and
 * timing. Matches on code plus funnel stage; falls back to the code alone when
 * the stage grouping differs. `null` for non-stage-error issues.
 */
export function stageErrorForIssue(issue: FunnelIssue, errors: StageError[]): StageError | null {
  if (!issue.id?.startsWith(STAGE_ERROR_ISSUE_PREFIX) || !issue.code) return null;
  return (
    errors.find((e) => e.code === issue.code && funnelStageOf(e.stage) === issue.stage) ??
    errors.find((e) => e.code === issue.code) ??
    null
  );
}

/** Per funnel stage: how many current ERRORs and WARNINGs sit on it (stage chips, §5.2). */
export interface StageErrorTally {
  errors: number;
  warnings: number;
}

/** Tallies current stage errors by funnel stage key. */
export function tallyStageErrors(errors: StageError[]): Map<string, StageErrorTally> {
  const tally = new Map<string, StageErrorTally>();
  for (const error of errors) {
    const key = funnelStageOf(error.stage);
    const entry = tally.get(key) ?? { errors: 0, warnings: 0 };
    if (error.severity === 'ERROR') entry.errors += 1;
    else entry.warnings += 1;
    tally.set(key, entry);
  }
  return tally;
}
